import React, { FC } from "react";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import { PDF_LICENSE_STYLES } from "lib/constants/pdf-renderer-styles";

const LeaseLicense: FC<LicenseDetails> = ({
  licenseDate,
  licenseeName,
  licenseeAddress,
  designatedHours,
  licenseFee,
  consultingRoom,
  licensePeriodStart,
  carParkingFee,
  terminationDate,
}: LicenseDetails) => (
  <Document>
    <Page size="A4" style={PDF_LICENSE_STYLES.page}>
      <Text style={[PDF_LICENSE_STYLES.header, PDF_LICENSE_STYLES.bold]}>
        Consulting Room Licence Part Ground Floor 15-19 Church Road Stanmore HA7
        4AR
      </Text>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text style={PDF_LICENSE_STYLES.line}>
          <Text style={PDF_LICENSE_STYLES.semibold}>
            This licence is dated :
          </Text>{" "}
          {licenseDate || "____________________"}
        </Text>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text style={PDF_LICENSE_STYLES.semibold}>Parties</Text>

        <Text style={PDF_LICENSE_STYLES.line}>
          (1) Stanmore Wellness Clinic Limited (Company Number 10966593) of C/O
          Chamberlains UK LLP, 173 Cleveland Street, London, United Kingdom, W1T
          6QR (Licensor)
        </Text>

        <Text style={PDF_LICENSE_STYLES.line}>
          (2) {licenseeName || "__________________________________________"} of{" "}
          {licenseeAddress || "__________________________________________"}{" "}
          (Licensee)
        </Text>
      </View>

      <Text
        style={[
          PDF_LICENSE_STYLES.h2,
          PDF_LICENSE_STYLES.bold,
          PDF_LICENSE_STYLES.italic,
        ]}
      >
        Agreed Terms
      </Text>
      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
            PDF_LICENSE_STYLES.h2,
          ]}
        >
          1. Interpretation
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          The following definitions and rules of interpretation apply in this
          license.
        </Text>

        <Text style={[PDF_LICENSE_STYLES.bold, PDF_LICENSE_STYLES.paragraph]}>
          1.1 Definitions:
        </Text>

        {[
          {
            label: "• Building: ",
            value:
              "all that space known as Part Ground Floor 15-19 Church Road Stanmore HA7 4AR or such reduced or extended area as the Licensor may from time to time designate as comprising the Building",
          },
          {
            label: "• Common Parts: ",
            value:
              "such entrance halls, corridors, and other means of access in or upon the Building the use of which is necessary for obtaining access to and egress from the Consulting Room as designated from time to time by the Licensor.",
          },
          {
            label: "• Competent Authority: ",
            value:
              "any statutory undertaker or any statutory public local or other authority or regulatory body or any court of law or government department or any of them or any of their duly authorised officers.",
          },
          {
            label: "• Designated Hours: ",
            value:
              designatedHours ||
              "______________________________________________________________________________",
          },
          {
            label: "• license Fee: ",
            value: `the amount of £${
              licenseFee || "_____________________"
            } per day`,
          },
          {
            label: "• license Fee Commencement Date: ",
            value: "the date of this license",
          },
          {
            label: "• license Period: ",
            value: `the period from and including ${
              licensePeriodStart || "___________________________________"
            } until the date on which this license is determined in accordance with clause 4.`,
          },
          {
            label: "• Car Parking Fee £50 per month: ",
            value: carParkingFee || "Y / N Please circle.",
          },
          {
            label: "• Permitted Use: ",
            value:
              "office and/or consulting room for a qualified and regulated provider of medical and/or health services to patients.",
          },
          {
            label: "• Consulting Room: ",
            value: `Room ${
              consultingRoom || "_____________________"
            } at the Building or such other space as allocated to the Licensee from time to time under clause 2.2d).`,
          },
          {
            label: "• Service Media: ",
            value:
              "all media for the supply or removal of heat, electricity, gas, water, sewage, energy, telecommunications, data and all other services and utilities and all structures, machinery and equipment ancillary to those media.",
          },
          {
            label: "• VAT: ",
            value:
              "value added tax or any equivalent tax chargeable in the UK.",
          },
        ].map((item, index) => (
          <Text key={index} style={PDF_LICENSE_STYLES.paragraph}>
            <Text style={PDF_LICENSE_STYLES.semibold}>{item.label}</Text>
            {item.value}
          </Text>
        ))}

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>1.2 </Text>
          Unless the context otherwise requires, words in the singular shall
          include the plural and in the plural shall include the singular.
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>1.3 </Text>
          Unless expressly provided otherwise, the obligations and liabilities
          of the Licensee under this license are joint and several.
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>1.4 </Text>A working day is any
          day which is not a Saturday, a Sunday, a bank holiday or a public
          holiday in England.
        </Text>
      </View>
      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
          ]}
        >
          2. license to occupy
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>2.1 </Text>
          Subject to clause 3 and clause 4, the Licensor permits the Licensee to
          occupy the Consulting Room for the Permitted Use for the license
          Period during the Designated Hours in common with the Licensor and all
          others authorised by the Licensor (so far as is not inconsistent with
          the rights given to the Licensee to use the Consulting Room for the
          Permitted Use) together with the rights mentioned in Schedule 1.
        </Text>

        <Text style={[PDF_LICENSE_STYLES.bold, PDF_LICENSE_STYLES.paragraph]}>
          2.2 The Licensee acknowledges that:
        </Text>

        <View
          style={[
            PDF_LICENSE_STYLES.listItem,
            { flexDirection: "row", alignItems: "flex-start" },
          ]}
        >
          <Text style={PDF_LICENSE_STYLES.listBullet}>a)</Text>
          <Text style={PDF_LICENSE_STYLES.listContent}>
            the Licensee shall occupy the Consulting Room as a licensee and that
            no relationship of landlord and tenant is created between the
            Licensor and the Licensee by this license;
          </Text>
        </View>

        <View
          style={[
            PDF_LICENSE_STYLES.listItem,
            { flexDirection: "row", alignItems: "flex-start" },
          ]}
        >
          <Text style={PDF_LICENSE_STYLES.listBullet}>b)</Text>
          <Text style={PDF_LICENSE_STYLES.listContent}>
            the Licensor retains control, possession and management of the
            Consulting Room and the Licensee has no right to exclude the
            Licensor from the Consulting Room;
          </Text>
        </View>

        <View
          style={[
            PDF_LICENSE_STYLES.listItem,
            { flexDirection: "row", alignItems: "flex-start" },
          ]}
        >
          <Text style={PDF_LICENSE_STYLES.listBullet}>c)</Text>
          <Text style={PDF_LICENSE_STYLES.listContent}>
            the license to occupy granted by this agreement is personal to the
            Licensee and is not assignable and the rights given in clause 2 may
            only be exercised by the Licensee and its employees; and
          </Text>
        </View>

        <View
          style={[
            PDF_LICENSE_STYLES.listItem,
            { flexDirection: "row", alignItems: "flex-start" },
          ]}
        >
          <Text style={PDF_LICENSE_STYLES.listBullet}>d)</Text>
          <Text style={PDF_LICENSE_STYLES.listContent}>
            without prejudice to its rights under clause 4, the Licensor shall
            be entitled at any time on giving not less than 1 weeks' notice (or
            1 days’ notice if the license Period is shorter than 1 week) to
            require the Licensee to transfer to alternative space elsewhere
            within the Building and the Licensee shall comply with such
            requirement.
          </Text>
        </View>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
            { marginBottom: 6 },
          ]}
        >
          3. Licensee's obligations
        </Text>
        <Text style={PDF_LICENSE_STYLES.paragraph}>
          The Licensee agrees and undertakes:
        </Text>
        <View style={{ paddingLeft: 12, marginBottom: 8 }}>
          {/* a) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>a)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>to pay:</Text>
          </View>
          {/* (i) nested under a) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              {
                flexDirection: "row",
                alignItems: "flex-start",
                paddingLeft: 12,
              },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>(i)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              to the Licensor the license Fee payable without any deduction in
              advance on the date of this license
            </Text>
          </View>
          {/* b) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>b)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              to maintain such medical or professional qualification as is
              suitable and appropriate for services being provided at the
              Consulting Room (and consistent with the Permitted Use) and not to
              allow the same to lapse and not to allow the provision of medical
              or consulting services by unqualified persons unless fully and
              appropriately supervised.
            </Text>
          </View>
          {/* c) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>c)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              to maintain public liability and professional indemnity insurance
              as is sufficient and applicable to the services being provided at
              the Consulting Room.
            </Text>
          </View>
          {/* d) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>d)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              not to solicit the patients or visitors to other operators at the
              Building without the consent of such operators.
            </Text>
          </View>
          {/* (e) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>e)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              keep the Consulting Room clean, tidy and clear of rubbish;
            </Text>
          </View>
          {/* (f) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>f)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              not to use the Consulting Room other than for the Permitted Use;
            </Text>
          </View>
          {/* (g) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>g)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              not to make any alteration or addition whatsoever to the
              Consulting Room;
            </Text>
          </View>
          {/* (h) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>h)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              not to display any advertisement, signboards, nameplate,
              inscription, flag, banner, placard, poster, signs or notices at
              the Consulting Room or elsewhere in the Building
            </Text>
          </View>
          {/* (i) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>i)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              not to do or permit to be done on the Consulting Room anything
              which is illegal or which may be or become a nuisance (whether
              actionable or not), annoyance, inconvenience or disturbance to the
              Licensor or to tenants or occupiers of the Building or any owner
              or occupier of neighbouring property.
            </Text>
          </View>
          {/* (j) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>j)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              not to cause or permit to be caused any damage to:
            </Text>
          </View>
          {/* (i) nested under (j) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              {
                flexDirection: "row",
                alignItems: "flex-start",
                paddingLeft: 12,
              },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>(i)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              the Consulting Room, Building or any neighbouring property; or
            </Text>
          </View>
          {/* (ii) nested under (j) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              {
                flexDirection: "row",
                alignItems: "flex-start",
                paddingLeft: 12,
              },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>(ii)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              any chattels of the owners or occupiers of the Consulting Room,
              Building or any neighbouring property
            </Text>
          </View>
          {/* (k) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>k)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              not to obstruct the Common Parts, make them dirty or untidy or
              leave any rubbish on them;
            </Text>
          </View>
          {/* (l) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>l)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              to observe any rules and regulations the Licensor makes and
              notifies to the Licensee from time to time governing the
              Licensee's use of the Consulting Room and the Common Parts;
            </Text>
          </View>
          {/* (m) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>m) </Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              to leave the Consulting Room in a clean and tidy condition and to
              remove the Licensee's furniture equipment and goods from the
              Consulting Room at the end of the license Period;
            </Text>
          </View>
          {/* (n) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>n)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              to indemnify the Licensor and keep the Licensor indemnified
              against all losses, claims, demands, actions, proceedings,
              damages, costs, expenses or other liability in any way arising
              from:
            </Text>
          </View>
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              {
                flexDirection: "row",
                alignItems: "flex-start",
                paddingLeft: 12,
              },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>(i)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>this licence;</Text>
          </View>

          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              {
                flexDirection: "row",
                alignItems: "flex-start",
                paddingLeft: 12,
              },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>(ii)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              any breach of the Licensee's undertakings contained in clause 3;
              and/or
            </Text>
          </View>

          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              {
                flexDirection: "row",
                alignItems: "flex-start",
                paddingLeft: 12,
              },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>(iii)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              the exercise of any rights given in clause 2;
            </Text>
          </View>

          {/* (o) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>(o)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              (o) to pay to the Licensor interest on the Licence Fee or other
              payments at the rate of five per cent per annum above the base
              rate of Barclays Bank Plc from time to time calculated on a daily
              basis from the due date until payment if the Licensee shall fail
              to pay the Licence Fee or any other payments due under this
              licence by the due date (whether formally demanded or not).
            </Text>
          </View>
          {/* (p) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>p)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              The Licensee shall not, during the license Period, engage in any
              activities or enter into any agreements or arrangements, whether
              directly or indirectly, with any competitors of the Licensor,
              where "competitors" shall mean any individuals or entities
              offering services similar to those provided by the Licensor within
              a 10-mile radius of the Building or as otherwise designated by the
              Licensor.
            </Text>
          </View>

          {/* (q) */}
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>q)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              The Licensor reserves the right, at its sole discretion, to
              relocate the Licensee to a different Consulting Room within the
              Building upon providing not less than 7 days' written notice (or
              24 hours' notice if the license Period is shorter than 1 week).
              The new Consulting Room will be of a comparable size and
              suitability for the Permitted Use. The Licensee agrees to vacate
              the existing Consulting Room and move to the designated
              alternative Consulting Room as instructed by the Licensor within
              the specified notice period.
            </Text>
          </View>
        </View>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
          ]}
        >
          4. Termination
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>4.1</Text> This license shall
          end on the earliest of:
        </Text>

        <View style={PDF_LICENSE_STYLES.nestedListItem}>
          <View style={PDF_LICENSE_STYLES.listItem}>
            <Text style={PDF_LICENSE_STYLES.listBullet}>a)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              {terminationDate || "______________________________"}
            </Text>
          </View>

          <View style={PDF_LICENSE_STYLES.listItem}>
            <Text style={PDF_LICENSE_STYLES.listBullet}>b)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              On written notice given by the Licensor to the Licensee at any
              time on breach of any of the Licensee's obligations contained in
              clause 3
            </Text>
          </View>

          <View style={PDF_LICENSE_STYLES.listItem}>
            <Text style={PDF_LICENSE_STYLES.listBullet}>c)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>the expiry of:</Text>
          </View>

          <View style={PDF_LICENSE_STYLES.nestedListItemLevel2}>
            <View style={PDF_LICENSE_STYLES.listItem}>
              <Text style={PDF_LICENSE_STYLES.listBullet}>(i)</Text>
              <Text style={PDF_LICENSE_STYLES.listContent}>
                not less than 1 weeks’ notice in writing from the Licensor to
                the Licensee
              </Text>
            </View>

            <View style={PDF_LICENSE_STYLES.listItem}>
              <Text style={PDF_LICENSE_STYLES.listBullet}>(ii)</Text>
              <Text style={PDF_LICENSE_STYLES.listContent}>
                where date in Clause 4.1a) is less than 1 week after the date of
                this license, 24 hours’ notice in writing
              </Text>
            </View>
          </View>
        </View>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>4.2</Text> Termination of this
          license shall not affect the rights of either party in connection with
          any breach of any obligation under this license which existed at or
          before the date of termination.
        </Text>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
            { marginBottom: 6 },
          ]}
        >
          5. Notices
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>5.1 </Text>
          Any notice given to a party under or in connection with this license
          shall be in writing and shall be given by hand or by pre-paid
          first-class post or other next working day delivery service at its
          registered office (if a company) or its principal place of business
          (in any other case) or may be given by email.
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>5.2 </Text>
          If a notice or other communication complies with the criteria in
          clause 5.1, it shall be deemed to have been received:
        </Text>

        <View style={{ paddingLeft: 12, marginBottom: 8 }}>
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>a)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              if delivered by hand, at the time the notice or other
              communication is left at the proper address; or
            </Text>
          </View>

          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>b)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              if sent by pre-paid first-class post or other next working day
              delivery service, at noon on the working day after posting.
            </Text>
          </View>

          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>c)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              if sent by email, 1 hour after sending.
            </Text>
          </View>
        </View>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>5.3 </Text>
          This clause does not apply to the service of any proceedings or other
          documents in any legal action or, where applicable, any arbitration or
          other method of dispute resolution.
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>5.4 </Text>A notice or other
          communication given under this license is not valid if sent by e-mail
          or fax.
        </Text>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
            { marginBottom: 6 },
          ]}
        >
          6. No warranties for use or condition
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>6.1 </Text>
          The Licensor gives no warranty that the Consulting Room possesses the
          Necessary Consents for the Permitted Use.
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>6.2 </Text>
          The Licensor gives no warranty that the Consulting Room is physically
          fit for the purposes specified in clause 2.
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>6.3 </Text>
          The Licensee acknowledges that it does not rely on, and shall have no
          remedies in respect of, any representation or warranty (whether made
          innocently or negligently) that may have been made by or on behalf of
          the Licensor before the date of this license as to any of the matters
          mentioned in clause 6.1 or 6.2.
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>6.4 </Text>
          Nothing in this clause shall limit or exclude any liability for fraud.
        </Text>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
            { marginBottom: 6 },
          ]}
        >
          7. Limitation of Licensor's liability
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>7.1 </Text>
          Subject to clause 7.2 the Licensor is not liable for:
        </Text>

        <View style={{ paddingLeft: 12, marginBottom: 8 }}>
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>a)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              the death of, or injury to the Licensee, its employees, customers
              or invitees to the Consulting Room; or
            </Text>
          </View>

          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>b)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              damage to any property of the Licensee or that of the Licensee's
              employees, customers or other invitees to the Consulting Room; or
            </Text>
          </View>

          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>c)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              any losses, claims, demands, actions, proceedings, damages, costs
              or expenses or other liability incurred by Licensee or the
              Licensee's employees, customers or other invitees to the
              Consulting Room in the exercise or purported exercise of the
              rights granted by clause 2.
            </Text>
          </View>
        </View>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text style={PDF_LICENSE_STYLES.paragraph}>
          <Text style={PDF_LICENSE_STYLES.bold}>7.2 </Text>
          Nothing in clause 7.1 shall limit or exclude the Licensor's liability
          for:
        </Text>

        <View style={{ paddingLeft: 12, marginBottom: 8 }}>
          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>a)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              death or personal injury or damage to Consulting Room caused by
              negligence on the part of the Licensor or its employees or agents;
              or
            </Text>
          </View>

          <View
            style={[
              PDF_LICENSE_STYLES.listItem,
              { flexDirection: "row", alignItems: "flex-start" },
            ]}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>b)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              any matter in respect of which it would be unlawful for the
              Licensor to exclude or restrict liability.
            </Text>
          </View>
        </View>
      </View>

      <Text
        style={[
          PDF_LICENSE_STYLES.h2,
          PDF_LICENSE_STYLES.bold,
          PDF_LICENSE_STYLES.italic,
        ]}
      >
        8. Third party rights
      </Text>
      <View style={PDF_LICENSE_STYLES.section}>
        <Text>
          This license does not give rise to any rights under the Contracts
          (Rights of Third Parties) Act 1999 to enforce any term of this
          license.
        </Text>
      </View>

      <Text
        style={[
          PDF_LICENSE_STYLES.h2,
          PDF_LICENSE_STYLES.bold,
          PDF_LICENSE_STYLES.italic,
        ]}
      >
        9. Governing law
      </Text>
      <View style={PDF_LICENSE_STYLES.section}>
        <Text>
          This license and any dispute or claim (including non-contractual
          disputes or claims) arising out of or in connection with it or its
          subject matter or formation shall be governed by and construed in
          accordance with the law of England and Wales.
        </Text>
      </View>

      <Text
        style={[
          PDF_LICENSE_STYLES.h2,
          PDF_LICENSE_STYLES.bold,
          PDF_LICENSE_STYLES.italic,
        ]}
      >
        10. Jurisdiction
      </Text>
      <View style={PDF_LICENSE_STYLES.section}>
        <Text>
          Each party irrevocably agrees that the courts of England and Wales
          shall have exclusive jurisdiction to settle any dispute or claim
          (including non-contractual disputes or claims) arising out of or in
          connection with this license or its subject matter or formation.
        </Text>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
            { marginBottom: 6 },
          ]}
        >
          11. Acknowledgement
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          For the avoidance of doubt the Licensee acknowledges that:
        </Text>

        <View style={{ paddingLeft: 12, marginBottom: 8 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
            }}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>a)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              the Licensee shall occupy the Consulting Room as a licensee and
              that no relationship of landlord and tenant is created between the
              Licensor and the Licensee by this license;
            </Text>
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
            }}
          >
            <Text style={PDF_LICENSE_STYLES.listBullet}>b)</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              the Licensor retains control, possession and management of the
              Building and the Licensee has no right to exclude the Licensor
              from the Consulting Room.
            </Text>
          </View>
        </View>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          This license has been entered into on the date stated at the beginning
          of it.
        </Text>
      </View>

      <View style={PDF_LICENSE_STYLES.section}>
        <Text
          style={[
            PDF_LICENSE_STYLES.h2,
            PDF_LICENSE_STYLES.bold,
            PDF_LICENSE_STYLES.italic,
            { marginBottom: 6 },
          ]}
        >
          Schedule 1 Rights granted to Licensee
        </Text>

        <Text style={PDF_LICENSE_STYLES.paragraph}>
          1. The right for the Licensee to use during the Designated Hours:
        </Text>

        <View style={{ paddingLeft: 12 }}>
          <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
            <Text style={PDF_LICENSE_STYLES.listBullet}>1.1</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              Such parts of the Common Parts for the purpose of access to and
              egress from the Consulting Room as shall from time to time be
              designated by the Licensor for such purpose.
            </Text>
          </View>

          <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
            <Text style={PDF_LICENSE_STYLES.listBullet}>1.2</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              The Service Media serving the Consulting Room.
            </Text>
          </View>

          <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
            <Text style={PDF_LICENSE_STYLES.listBullet}>1.3</Text>
            <Text style={PDF_LICENSE_STYLES.listContent}>
              Any toilet facilities provided by the Licensor.
            </Text>
          </View>
        </View>
      </View>

      {/* <View style={PDF_LICENSE_STYLES.section}>
        <Text style={PDF_LICENSE_STYLES.line}>
          <Text style={PDF_LICENSE_STYLES.bold}>Signed by Licensor:</Text>{" "}
          __________________________
        </Text>
        <Text style={PDF_LICENSE_STYLES.line}>
          <Text style={PDF_LICENSE_STYLES.bold}>Signed by Licensee:</Text>{" "}
          __________________________
        </Text>
      </View> */}
    </Page>
  </Document>
);

export default LeaseLicense;
